import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { Badge } from '../../components/ui/badge';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import { Leaf, RefreshCw, Download, FileText, Car, Trees, Fuel, Info, Lightbulb, Flame, Waves, ChevronRight, ChevronDown, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { equivalenciesFromKg } from '../../lib/emissions';
import { dedupeUomLabels } from '../../lib/uom';
import useEmissionFactors from '../../hooks/useEmissionFactors';
import EmissionsTargets from '../../components/sustainability/EmissionsTargets';
import EsgSnapshotCard from '../../components/sustainability/EsgSnapshotCard';

const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d });
const co2 = (kg) => (kg >= 1000 ? `${fmt(kg / 1000, 2)} t` : `${fmt(kg, 0)} kg`);

function monthLabel(m) {
  if (!m || m.length < 7) return m;
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }).replace(' ', " '");
}

const RANGES = [{ label: '6M', m: 6 }, { label: '12M', m: 12 }, { label: '24M', m: 24 }];

export default function EmissionsReport() {
  const currentAccountStr = typeof window !== 'undefined' ? localStorage.getItem('current_account') : null;
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  const accountId = currentAccount?.id;

  const [months, setMonths] = useState(12);
  const [usage, setUsage] = useState(null);
  const [emissions, setEmissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSite, setExpandedSite] = useState(null);
  const [trends, setTrends] = useState({});
  const [trendLoading, setTrendLoading] = useState(null);
  const [esgLoading, setEsgLoading] = useState(false);

  // Ensure DB-backed factors are loaded (single-bill viewers use them too).
  useEmissionFactors();

  const params = useMemo(() => {
    const to = new Date();
    const from = new Date(); from.setMonth(from.getMonth() - months);
    return { account_id: accountId, date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) };
  }, [accountId, months]);

  const loadAll = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [u, e] = await Promise.allSettled([
        api.get('/dashboard/org/mongo/commodity-usage', { params }),
        api.get('/dashboard/org/mongo/emissions', { params: { ...params, by_month: true } }),
      ]);
      if (u.status === 'fulfilled') setUsage(u.value.data);
      if (e.status === 'fulfilled') setEmissions(e.value.data);
      if (u.status === 'rejected' && e.status === 'rejected') toast.error('Failed to load emissions data.');
    } finally { setLoading(false); }
  }, [accountId, params]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleProperty = useCallback(async (siteUid) => {
    if (!siteUid) return;
    if (expandedSite === siteUid) { setExpandedSite(null); return; }
    setExpandedSite(siteUid);
    if (!trends[siteUid]) {
      setTrendLoading(siteUid);
      try {
        const { data } = await api.get('/dashboard/org/mongo/emissions/property-trend', {
          params: { ...params, site_uid: siteUid },
        });
        setTrends((prev) => ({ ...prev, [siteUid]: data.series || [] }));
      } catch {
        toast.error('Could not load this property’s trend.');
      } finally { setTrendLoading(null); }
    }
  }, [expandedSite, trends, params]);

  // All numbers below come from the backend region + vintage-aware engine.
  const totals = emissions?.totals || {};
  const totalKg = totals.co2e_kg || 0;
  const totalMt = totalKg / 1000;
  const eq = useMemo(() => equivalenciesFromKg(totalKg), [totalKg]);
  const trend = useMemo(
    () => (emissions?.monthly_totals || []).map((s) => ({ month: s.month, co2e: +((s.co2e_kg || 0) / 1000).toFixed(3) })),
    [emissions],
  );
  const props = emissions?.by_property || [];
  const regionsUsed = emissions?.regions_used || [];
  const maxCo2 = props.length ? props[0].co2e_kg : 0;

  const byCommodity = [
    { key: 'electricity', label: 'Electricity', icon: Lightbulb, kg: totals.electricity_kg || 0, color: '#fb7185' },
    { key: 'gas', label: 'Natural Gas', icon: Flame, kg: totals.gas_kg || 0, color: '#fb923c' },
    { key: 'water', label: 'Water / Sewer', icon: Waves, kg: totals.water_kg || 0, color: '#38bdf8' },
  ];

  const downloadCsv = () => {
    const header = ['Property', 'State', 'Region', 'Subregion', 'Factor vintage', 'Elec factor kg/kWh', 'Electricity kWh', 'Gas therms', 'Water gallons', 'CO2e kg'];
    const rows = props.map((p) => [p.sitename, p.state || '', p.region || '', p.subregion || '', p.factor_vintage || '', p.electricity_factor ?? '', Math.round(p.electricity_kwh), Math.round(p.gas_therms), Math.round(p.water_gallons), Math.round(p.co2e_kg)]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `emissions-by-property-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const downloadEsg = async () => {
    setEsgLoading(true);
    try {
      const res = await api.get('/dashboard/org/mongo/emissions/esg-report', { params, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `ESG-Summary-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Could not generate the ESG PDF.');
    } finally { setEsgLoading(false); }
  };

  if (!accountId) {
    return <MainLayout><div className="p-8 text-center text-muted-foreground">No account selected.</div></MainLayout>;
  }

  return (
    <MainLayout>
      <TooltipProvider delayDuration={150}>
        <div className="space-y-4 p-4" data-testid="emissions-report-page">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-xl font-semibold text-slate-900">
              <Leaf className="h-5 w-5 text-emerald-600" />
              Sustainability — Emissions Report
            </div>
            <Button variant="ghost" size="sm" className="h-9 gap-1.5 text-muted-foreground hover:text-slate-900" onClick={loadAll} disabled={loading} data-testid="emissions-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <div className="ml-auto flex items-center gap-1">
              {RANGES.map((r) => (
                <Button key={r.label} size="sm" variant={months === r.m ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setMonths(r.m)} data-testid={`emissions-range-${r.label}`}>
                  {r.label}
                </Button>
              ))}
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs ml-2" onClick={downloadCsv} data-testid="emissions-download">
                <Download className="h-3 w-3" /> CSV
              </Button>
              <Button size="sm" className="h-8 gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={downloadEsg} disabled={esgLoading} data-testid="emissions-esg-export">
                <FileText className="h-3 w-3" /> {esgLoading ? 'Building…' : 'ESG PDF'}
              </Button>
            </div>
          </div>

          {/* Monthly trend — moved to top so the chart is visible without scrolling */}
          <Card className="border-slate-200/70 shadow-none" data-testid="emissions-trend">
            <CardContent className="p-4">
              <div className="text-sm font-semibold text-slate-900 mb-3">Monthly CO₂e trend</div>
              <div className="h-[280px]">
                {trend.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trend} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="co2grad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={monthLabel} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}t`} />
                      <RTooltip formatter={(v) => [`${fmt(v, 2)} t CO₂e`, 'Emissions']} labelFormatter={monthLabel}
                        contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                      <Area type="monotone" dataKey="co2e" stroke="#059669" strokeWidth={2} fill="url(#co2grad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">No usage data in the selected period</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Hero + equivalencies */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-1 overflow-hidden border-0" data-testid="emissions-hero">
              <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 px-6 py-7 text-white h-full">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider opacity-90">
                  <Leaf className="h-4 w-4" /> Total carbon footprint
                </div>
                <div className="mt-2 text-4xl font-black tracking-tight" data-testid="emissions-hero-total">
                  {loading && !emissions ? '…' : `${totalKg >= 1000 ? `${fmt(totalMt, 2)} t` : `${fmt(totalKg, 0)} kg`} CO₂e`}
                </div>
                <div className="mt-1 text-xs opacity-80">
                  {loading && !emissions ? 'Loading…' : `across last ${months} months · ${props.length} propert${props.length === 1 ? 'y' : 'ies'}`}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <HeroEq icon={Car} value={`${fmt(eq.miles)}`} label="miles driven" />
                  <HeroEq icon={Trees} value={fmt(eq.seedlings)} label="tree seedlings" />
                  <HeroEq icon={Fuel} value={`${fmt(eq.gasoline_gal)}`} label="gal gasoline" />
                </div>
              </div>
            </Card>

            {/* By commodity */}
            <Card className="lg:col-span-2 border-slate-200/70 shadow-none" data-testid="emissions-by-commodity">
              <CardContent className="p-4">
                <div className="text-sm font-semibold text-slate-900 mb-3">By commodity</div>
                <div className="space-y-3">
                  {byCommodity.map((c) => {
                    const pct = totalKg > 0 ? (c.kg / totalKg) * 100 : 0;
                    return (
                      <div key={c.key} data-testid={`emissions-commodity-${c.key}`}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="flex items-center gap-1.5 font-medium text-slate-700"><c.icon className="h-3.5 w-3.5" style={{ color: c.color }} />{c.label}</span>
                          <span className="font-mono font-semibold text-slate-900">{co2(c.kg)} · {fmt(pct, 0)}%</span>
                        </div>
                        {(() => {
                          const asBilled = dedupeUomLabels(usage?.original_uom_by_commodity?.[c.key]);
                          return asBilled.length > 0 ? (
                            <div className="mb-1 text-[10px] text-slate-400" data-testid={`emissions-asbilled-${c.key}`}>
                              As-billed: {asBilled.slice(0, 3).join(' · ')}{asBilled.length > 3 ? ' · …' : ''}
                            </div>
                          ) : null;
                        })()}
                        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c.color }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Region factor transparency */}
                {regionsUsed.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-3" data-testid="emissions-regions-used">
                    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <Globe className="h-3.5 w-3.5" /> Factors applied by region
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {regionsUsed.map((r) => (
                        <Tooltip key={r.region}>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="gap-1.5 border-emerald-200 bg-emerald-50 text-emerald-800" data-testid={`emissions-region-chip-${r.region}`}>
                              {r.region} · {r.vintage_year || '—'}
                              <span className="font-mono text-[10px] text-emerald-600">{r.property_count} · {co2(r.co2e_kg)}</span>
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">{r.label}</TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Emissions Targets */}
          <EmissionsTargets accountId={accountId} />

          {/* Monthly ESG snapshot email */}
          <EsgSnapshotCard accountId={accountId} />

          {/* By property */}
          <Card className="border-slate-200/70 shadow-none" data-testid="emissions-by-property">
            <CardContent className="p-4">
              <div className="text-sm font-semibold text-slate-900 mb-3">Emissions by property</div>
              <div className="max-h-[420px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="text-[10px] uppercase">Property</TableHead>
                      <TableHead className="text-[10px] uppercase">State</TableHead>
                      <TableHead className="text-[10px] uppercase">Factor (region · vintage)</TableHead>
                      <TableHead className="text-[10px] uppercase text-right">Electricity</TableHead>
                      <TableHead className="text-[10px] uppercase text-right">Gas</TableHead>
                      <TableHead className="text-[10px] uppercase text-right">Water</TableHead>
                      <TableHead className="text-[10px] uppercase text-right">CO₂e</TableHead>
                      <TableHead className="text-[10px] uppercase w-28">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {props.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-400 py-8">No property emissions in this period.</TableCell></TableRow>
                    )}
                    {props.map((p, i) => {
                      const isOpen = expandedSite === p.site_uid;
                      const series = trends[p.site_uid] || [];
                      return (
                        <React.Fragment key={p.site_uid || i}>
                          <TableRow
                            className="cursor-pointer hover:bg-slate-50"
                            data-testid={`emissions-property-${i}`}
                            onClick={() => toggleProperty(p.site_uid)}
                          >
                            <TableCell className="text-xs">
                              <span className="inline-flex items-center gap-1" data-testid={`emissions-property-toggle-${i}`}>
                                {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                                <span className={p.unmapped ? 'italic text-amber-700' : 'text-slate-800'}>{p.sitename || '—'}</span>
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">{p.state || '—'}</TableCell>
                            <TableCell className="text-[11px]" data-testid={`emissions-property-factor-${i}`}>
                              <span className="font-mono text-slate-600">{(p.electricity_factor ?? 0)} kg/kWh</span>
                              <span className="ml-1 text-slate-400">{p.region}{p.subregion ? `·${p.subregion}` : ''} · {p.factor_vintage || '—'}</span>
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">{fmt(p.electricity_kwh)} kWh</TableCell>
                            <TableCell className="text-xs text-right font-mono">{fmt(p.gas_therms)} th</TableCell>
                            <TableCell className="text-xs text-right font-mono">{fmt(p.water_gallons)} gal</TableCell>
                            <TableCell className="text-xs text-right font-mono font-semibold text-emerald-700">{co2(p.co2e_kg)}</TableCell>
                            <TableCell>
                              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${maxCo2 > 0 ? (p.co2e_kg / maxCo2) * 100 : 0}%` }} />
                              </div>
                            </TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow data-testid={`emissions-property-trend-${i}`}>
                              <TableCell colSpan={8} className="bg-slate-50/70 p-0">
                                <div className="p-4">
                                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Monthly CO₂e trend — {p.sitename || '—'}
                                  </div>
                                  {trendLoading === p.site_uid ? (
                                    <div className="py-8 text-center text-xs text-slate-400">Loading monthly trend…</div>
                                  ) : series.length ? (
                                    <div className="h-[200px]">
                                      <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={series.map((s) => ({ month: s.month, co2e: +(s.co2e_kg / 1000).toFixed(3), elec: Math.round(s.electricity_kwh), gas: Math.round(s.gas_therms), water: Math.round(s.water_gallons) }))} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                                          <defs>
                                            <linearGradient id={`pgrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                                              <stop offset="0%" stopColor="#0d9488" stopOpacity={0.4} />
                                              <stop offset="100%" stopColor="#0d9488" stopOpacity={0.02} />
                                            </linearGradient>
                                          </defs>
                                          <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                                          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={monthLabel} axisLine={false} tickLine={false} />
                                          <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}t`} />
                                          <RTooltip formatter={(v, n) => (n === 'co2e' ? [`${fmt(v, 2)} t CO₂e`, 'Emissions'] : [v, n])} labelFormatter={monthLabel} contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                                          <Area type="monotone" dataKey="co2e" stroke="#0d9488" strokeWidth={2} fill={`url(#pgrad-${i})`} />
                                        </AreaChart>
                                      </ResponsiveContainer>
                                    </div>
                                  ) : (
                                    <div className="py-8 text-center text-xs text-slate-400">No monthly usage recorded for this property in the selected range.</div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Footnote */}
          <div className="flex items-start gap-1.5 text-[11px] text-slate-500">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Emissions are computed <b>per property</b> using the factor set matched to that property’s
              <b> country &amp; state</b> and the <b>vintage</b> closest to each bill’s year — US uses EPA <b>eGRID</b>
              subregion electricity + <b>GHG Emission Factors Hub</b> (gas/fuels); Australia uses <b>DCCEEW NGA</b>.
              Water/sewer use an embedded-energy estimate. Equivalencies via the EPA <b>GHG Equivalencies Calculator</b>.
              Factor sets are managed in Super Admin → Emission Factors.
            </span>
          </div>
        </div>
      </TooltipProvider>
    </MainLayout>
  );
}

const HeroEq = ({ icon: Icon, value, label }) => (
  <div className="bg-white/15 rounded-md p-2 text-center">
    <Icon className="h-4 w-4 mx-auto mb-1 opacity-90" />
    <div className="text-sm font-bold leading-tight">{value}</div>
    <div className="text-[9px] uppercase tracking-wide opacity-80">{label}</div>
  </div>
);
